/*eslint no-unused-vars: 0*/
import React, { PureComponent } from 'react';
import History from './history';
import { uuid4 } from './utils';
import Select from './select';
import Pencil from './pencil';
import Line from './line';
import Rectangle from './rectangle';
import Circle from './circle';
import Pan from './pan';
import Pointer from './pointer';
import Nomal from './nomal';
import Tool from './tools';

import { fabric } from 'fabric';

class SketchField extends PureComponent {
  static defaultProps = {
    lineColor: 'black',
    lineWidth: 10,
    fillColor: 'transparent',
    backgroundColor: 'transparent',
    opacity: 1.0,
    undoSteps: 25,
    tool: Tool.Pencil,
    widthCorrection: 2,
    heightCorrection: 0,
    forceValue: false,
  };

  state = { parentWidth: 550, action: true };

  _initTools = (fabricCanvas) => {
    this._tools = {};
    this._tools[Tool.Select] = new Select(fabricCanvas);
    this._tools[Tool.Pencil] = new Pencil(fabricCanvas);
    this._tools[Tool.Line] = new Line(fabricCanvas);
    this._tools[Tool.Rectangle] = new Rectangle(fabricCanvas);
    this._tools[Tool.Circle] = new Circle(fabricCanvas);
    this._tools[Tool.Pan] = new Pan(fabricCanvas);
    this._tools[Tool.Pointer] = new Pointer(fabricCanvas);
    this._tools[Tool.Nomal] = new Nomal(fabricCanvas);
  };

  setSize = (width, height) => {
    this.width = width;
    this.height = height;
    if (this._fc) {
      this._fc.setWidth(width);
      this._fc.setHeight(height);
      this._fc.renderAll();
    }
  };

  /**
   * fabric.js 캔버스에 이미지를 추가합니다.
   * 원본 wehago-conference SketchField.addImg() 동일 로직.
   */
  addImg = (dataUrl, options = {}) => {
    const canvas = this._fc;
    if (!canvas) return;

    const { setLoading } = this.props;
    if (setLoading) setLoading(true);

    fabric.Image.fromURL(dataUrl, (oImg, isError) => {
      if (isError || !oImg) {
        console.error('fabric.Image.fromURL failed:', dataUrl);
        if (setLoading) setLoading(false);
        return;
      }

      const dividingWidth = (this.width || canvas.getWidth()) / oImg.width;
      const dividingHeight = (this.height || canvas.getHeight()) / oImg.height;
      const calcScale = dividingWidth < dividingHeight ? dividingWidth : dividingHeight;
      const scale = calcScale < 1 ? calcScale : 1;

      const resultSize = {
        width: oImg.width * scale,
        height: oImg.height * scale,
      };

      if (this.props.onChangeBackgroundImageSize) {
        this.props.onChangeBackgroundImageSize(resultSize);
      }

      let opts = { left: 0, top: 0, scale };
      Object.assign(opts, options);
      oImg.scale(opts.scale);
      oImg.set({ left: opts.left, top: opts.top, selectable: false, evented: false });

      canvas.add(oImg);
      if (setLoading) setLoading(false);
    }, { crossOrigin: 'anonymous' });
  };

  enableTouchScroll = () => {
    if (!this._fc.allowTouchScrolling) this._fc.allowTouchScrolling = true;
  };

  disableTouchScroll = () => {
    if (this._fc.allowTouchScrolling) this._fc.allowTouchScrolling = false;
  };

  _onObjectAdded = (e) => {
    if (!this.state.action) {
      this.setState({ action: true });
      return;
    }
    let obj = e.target;
    obj.__version = 1;
    obj.selectable = false;
    let objState = obj.toJSON();
    obj.__originalState = objState;
    let state = JSON.stringify(objState);
    this._history.keep([obj, state, state]);
  };

  _onObjectMoving = () => {};
  _onObjectScaling = () => {};
  _onObjectRotating = () => {};

  _onObjectModified = (e) => {
    let obj = e.target;
    obj.__version += 1;
    let prevState = JSON.stringify(obj.__originalState);
    let objState = obj.toJSON();
    obj.__originalState = objState;
    let currState = JSON.stringify(objState);
    this._history.keep([obj, prevState, currState]);
  };

  _onObjectRemoved = (e) => {
    let obj = e.target;
    if (obj.__removed) { obj.__version += 1; return; }
    obj.__version = 0;
  };

  _onMouseDown = (e) => {
    this._selectedTool.doMouseDown(e);
    if (this.props.onMouseDown) this.props.onMouseDown(e);
  };

  _onMouseMove = (e) => this._selectedTool.doMouseMove(e);

  _onMouseOut = (e) => {
    this._selectedTool.doMouseOut(e);
    if (this.props.onChange) {
      let onChange = this.props.onChange;
      setTimeout(() => onChange(e.e), 10);
    }
  };

  _onMouseUp = (e) => {
    this._selectedTool.doMouseUp(e);
    const canvas = this._fc;
    if (this.props.tool !== Tool.Pencil) {
      const objects = canvas.getObjects();
      const newObj = objects[objects.length - 1];
      if (newObj && newObj.__version === 1) newObj.__originalState = newObj.toJSON();
    }
    const context = canvas.contextTop;
    context.clearRect(0, 0, context.canvas.width, context.canvas.height);
    if (this.props.onChange) {
      let onChange = this.props.onChange;
      setTimeout(() => onChange(e.e), 10);
    }
  };

  _resize = (e) => {
    if (e) e.preventDefault();
    let { widthCorrection, heightCorrection } = this.props;
    let canvas = this._fc;
    let { offsetWidth, clientHeight } = this._container;
    let prevWidth = canvas.getWidth();
    let prevHeight = canvas.getHeight();
    let wfactor = ((offsetWidth - widthCorrection) / prevWidth).toFixed(2);
    let hfactor = ((clientHeight - heightCorrection) / prevHeight).toFixed(2);
    canvas.setWidth(offsetWidth - widthCorrection);
    canvas.setHeight(clientHeight - heightCorrection);
    if (canvas.backgroundImage) {
      let bi = canvas.backgroundImage;
      bi.width = bi.width * wfactor;
      bi.height = bi.height * hfactor;
    }
    let objects = canvas.getObjects();
    for (let i in objects) {
      let obj = objects[i];
      obj.scaleX = obj.scaleX * wfactor;
      obj.scaleY = obj.scaleY * hfactor;
      obj.left = obj.left * wfactor;
      obj.top = obj.top * hfactor;
      obj.setCoords();
    }
    this.setState({ parentWidth: offsetWidth });
    canvas.renderAll();
    canvas.calcOffset();
  };

  _backgroundColor = (color) => {
    if (!color) return;
    this._fc.setBackgroundColor(color, () => this._fc.renderAll());
  };

  zoom = (factor) => {
    let canvas = this._fc;
    let objects = canvas.getObjects();
    for (let i in objects) {
      objects[i].scaleX *= factor;
      objects[i].scaleY *= factor;
      objects[i].left *= factor;
      objects[i].top *= factor;
      objects[i].setCoords();
    }
    canvas.renderAll();
    canvas.calcOffset();
  };

  undo = () => {
    let history = this._history;
    let [obj, prevState] = history.getCurrent();
    history.undo();
    if (obj.__removed) {
      this.setState({ action: false }, () => {
        this._fc.add(obj);
        obj.__version -= 1;
        obj.__removed = false;
      });
    } else if (obj.__version <= 1) {
      this._fc.remove(obj);
    } else {
      obj.__version -= 1;
      obj.setOptions(JSON.parse(prevState));
      obj.setCoords();
    }
    this._fc.renderAll();
    if (this.props.onChange) this.props.onChange({ type: 'undo' });
  };

  redo = () => {
    let history = this._history;
    if (history.canRedo()) {
      let canvas = this._fc;
      let [obj, prevState, currState] = history.redo();
      if (obj.__version === 0) {
        this.setState({ action: false }, () => { canvas.add(obj); obj.__version = 1; });
      } else {
        obj.__version += 1;
        obj.setOptions(JSON.parse(currState));
      }
      obj.setCoords();
      canvas.renderAll();
      if (this.props.onChange) this.props.onChange({ type: 'redo' });
    }
  };

  canUndo = () => this._history.canUndo();
  canRedo = () => this._history.canRedo();

  toDataURL = (options) => this._fc.toDataURL(options);
  toJSON = (propertiesToInclude) => this._fc.toJSON(propertiesToInclude);

  fromJSON = (json) => {
    if (!json) return;
    let canvas = this._fc;
    setTimeout(() => {
      canvas.loadFromJSON(json, () => {
        canvas.renderAll();
        if (this.props.onChange) this.props.onChange();
      });
    }, 100);
  };

  clear = (propertiesToInclude) => {
    let discarded = this.toJSON(propertiesToInclude);
    this._fc.clear();
    this._history.clear();
    if (this.props.onChange) this.props.onChange({ type: 'clear' });
    return discarded;
  };

  removeSelected = () => {
    let canvas = this._fc;
    let activeObj = canvas.getActiveObject();
    if (activeObj) {
      let selected = [];
      if (activeObj.type === 'activeSelection') {
        activeObj.forEachObject((obj) => selected.push(obj));
      } else {
        selected.push(activeObj);
      }
      selected.forEach((obj) => {
        obj.__removed = true;
        let objState = obj.toJSON();
        obj.__originalState = objState;
        let state = JSON.stringify(objState);
        this._history.keep([obj, state, state]);
        canvas.remove(obj);
      });
      canvas.discardActiveObject();
      canvas.requestRenderAll();
    }
  };

  componentDidMount = () => {
    let { tool, value, undoSteps, defaultValue, backgroundColor } = this.props;

    let canvas = (this._fc = new fabric.Canvas(this._canvas));
    this._initTools(canvas);
    this._backgroundColor(backgroundColor);

    let selectedTool = this._tools[tool];
    selectedTool.configureCanvas(this.props);
    this._selectedTool = selectedTool;

    window.addEventListener('resize', this._resize, false);
    this._history = new History(undoSteps);
    window._fc = this._fc;

    canvas.on('object:added', this._onObjectAdded);
    canvas.on('object:modified', this._onObjectModified);
    canvas.on('object:removed', this._onObjectRemoved);
    canvas.on('mouse:down', this._onMouseDown);
    canvas.on('mouse:move', this._onMouseMove);
    canvas.on('mouse:up', this._onMouseUp);
    canvas.on('mouse:out', this._onMouseOut);
    canvas.on('object:moving', this._onObjectMoving);
    canvas.on('object:scaling', this._onObjectScaling);
    canvas.on('object:rotating', this._onObjectRotating);

    this.disableTouchScroll();
    this._resize();

    (value || defaultValue) && this.fromJSON(value || defaultValue);
  };

  componentWillUnmount = () => window.removeEventListener('resize', this._resize);

  componentDidUpdate = (prevProps) => {
    if (
      this.props.width !== prevProps.width ||
      this.props.height !== prevProps.height
    ) {
      this._resize();
    }

    if (this.props.tool !== prevProps.tool) {
      this._selectedTool = this._tools[this.props.tool] || this._tools[Tool.Pencil];
    }

    this._fc.defaultCursor = 'default';
    this._selectedTool.configureCanvas(this.props);

    if (this.props.backgroundColor !== prevProps.backgroundColor) {
      this._backgroundColor(this.props.backgroundColor);
    }

    if (
      this.props.value !== prevProps.value ||
      (this.props.value && this.props.forceValue)
    ) {
      this.fromJSON(this.props.value);
    }
  };

  render = () => {
    let { className, style, width, height } = this.props;
    let canvasDivStyle = Object.assign(
      {},
      style ? style : {},
      width ? { width: width } : {},
      height ? { height: height } : { height: 512 },
    );
    return (
      <div className={className} ref={(c) => (this._container = c)} style={canvasDivStyle}>
        <canvas id={uuid4()} ref={(c) => (this._canvas = c)}>
          Sorry, Canvas HTML5 element is not supported by your browser :(
        </canvas>
      </div>
    );
  };
}

export default SketchField;
